import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Paging from '../../component/Paging';
import request from '../../../utils/Request.utils';
import { ToastContainer, toast } from 'react-toastify';
import Modal from '../../component/Modal';

const Inquiry = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [inquiryTitleList, setInquiryTitleList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currPage, setCurrPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [loading, setLoading] = useState(true);

  // 검색
  const [searchQuery, setSearchQuery] = useState('');
  const [inputQuery, setInputQuery] = useState(''); // 입력 필드 상태
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedStatus, setSelectedStatus] = useState('전체');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');

  // 선택된 항목
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const getInquiryTitleInfo = () => {
    let params = {};

    const successHandler = (response) => {
      if (response.messageCode === 200) {
        setInquiryTitleList(response.data.data_list);
      } else {
        console.log("문의 유형정보를 불러올 수 없습니다.");
        toast.error("문의 유형정보를 불러올 수 없습니다.");
      }
    };
    const failHandler = (error) => {
      console.log(error);
    };

    request.get("/common/code/board_title_ty", params, successHandler, failHandler);
  };

  const successHandler = useCallback((response) => {
    if (response.messageCode === 200) {
      setInquiries(response.data.dataList);
      setTotalPages(response.data.tot_page);
      setTotalCnt(response.data.totalCnt);
      setLoading(false);
    } else {
      toast.error("문의사항 정보를 불러올 수 없습니다.");
      setLoading(false);
    }
  }, []);

  const errorHandler = useCallback((error) => {
    toast.error("데이터를 불러오는 중 오류가 발생했습니다.");
    setLoading(false);
  }, []);

  const fetchInquiries = useCallback(() => {
    setLoading(true);
    const params = {
      curr_page: currPage,
      per_page: 10,
      sort_col: 'reg_dtm',
      sort_by: 'desc',
      board_stat: selectedStatus === '전체' ? '' : selectedStatus,
      search_query: searchQuery,
      search_board_cate: selectedCategory === '전체' ? '' : selectedCategory,
      search_start_date: searchStartDate,
      search_end_date: searchEndDate,
    };
    request.get("/board/list/admin/BD_INQUIRY", params, successHandler, errorHandler);
  }, [currPage, searchQuery, selectedCategory, selectedStatus, searchStartDate, searchEndDate, successHandler, errorHandler]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries, currPage]);

  useEffect(() => {
    getInquiryTitleInfo();
  }, []);

  const handlePageChange = (page) => {
    setCurrPage(page);
  };

  const handleSearch = () => {
    if (searchStartDate && searchEndDate) {
      fetchInquiries();
    } else {
      toast.error("시작일과 종료일을 모두 입력해주세요.");
    }
  };

  const handleReset = () => {
    setInputQuery('');
    setSearchQuery('');
    setSelectedCategory('전체');
    setSelectedStatus('전체');
    setSearchStartDate('');
    setSearchEndDate('');
    setCurrPage(1);
    fetchInquiries();
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleSelectInquiry = (inquiryId) => {
    setSelectedInquiries(prevSelectedInquiries =>
      prevSelectedInquiries.includes(inquiryId)
        ? prevSelectedInquiries.filter(id => id !== inquiryId)
        : [...prevSelectedInquiries, inquiryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedInquiries.length === inquiries.length) {
      setSelectedInquiries([]);
    } else {
      setSelectedInquiries(inquiries.map(inquiry => inquiry.board_seq));
    }
  };

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);

  const handleDelete = async () => {
    try {
      await request.del("/board/BD_INQUIRY", { board_seqs: selectedInquiries }, () => {
        toast.success("문의사항이 성공적으로 삭제되었습니다.");
        fetchInquiries();
        setSelectedInquiries([]);
      }, () => {
        toast.error("문의사항 삭제 중 오류가 발생했습니다.");
      });
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  const handleDateRangeClick = (rangeType) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (rangeType) {
      case 'today':
        // start and end are already set to today
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setDate(1);
        break;
      default:
        break;
    }

    setSearchStartDate(start.toISOString().split('T')[0]);
    setSearchEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">문의목록</h2>
      <div className="adm_com_pg_content">
        <div className="adm_com_board_list__top">
          <div className="adm_com_date_select">
            <span className="tit">기간설정 : </span>
            <input
              type="date"
              className="adm_com_datepicker"
              value={searchStartDate}
              onChange={(e) => setSearchStartDate(e.target.value)}
            />
            <span className="divider"> ~ </span>
            <input
              type="date"
              className="adm_com_datepicker"
              value={searchEndDate}
              onChange={(e) => setSearchEndDate(e.target.value)}
            />
            <button className="adm_com_btn black" onClick={() => handleDateRangeClick('today')}>오늘</button>
            <button className="adm_com_btn gray" onClick={() => handleDateRangeClick('yesterday')}>어제</button>
            <button className="adm_com_btn gray" onClick={() => handleDateRangeClick('week')}>일주일</button>
            <button className="adm_com_btn gray" onClick={() => handleDateRangeClick('month')}>이번달</button>
          </div>
          <div className="adm_com_board_search">
            <div className="adm_com_input_wrap flex_row">
              <span className="tit">문의분류 : </span>
              <select className="select" value={selectedCategory} onChange={handleCategoryChange}>
                <option value="전체">전체</option>
                {inquiryTitleList.map((title) => (
                  <option key={title.comm_code} value={title.comm_code}>
                    {title.code_nm}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="adm_com_board_search">
            <div className="adm_com_input_wrap flex_row">
              <span className="tit">처리상태 : </span>
              <select className="select" value={selectedStatus} onChange={handleStatusChange}>
                <option value="전체">전체</option>
                <option value="대기">대기</option>
                <option value="완료">완료</option>
              </select>
            </div>
          </div>
          <div className="adm_com_board_search">
            <div className="adm_com_input_wrap flex_row">
              <span className="tit">상세조회 : </span>
              <article className='search'>
                <input
                  type="search"
                  value={inputQuery}
                  onChange={(e) => setInputQuery(e.target.value)}
                  placeholder='검색할 내용을 입력해 주세요.'
                  className="input s type_search"
                />
                <button className="adm_com_btn blue" onClick={handleSearch}>조회</button>
                <button className="adm_com_btn gray" onClick={handleReset}>초기화</button>
              </article>
            </div>
          </div>
        </div>
        <div className="adm_com_btn_wrap">
          <button className="adm_com_btn gray" onClick={openModal}>선택삭제</button>
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <table className='adm_com_board_list'>
            <colgroup>
              <col width="5%" />
              <col width="5%" />
              <col width="5%" />
              <col width="40%" />
              <col width="10%" />
              <col width="20%" />
              <col width="15%" />
              <col width="5%" />
              <col width="10%" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="check">
                    <input
                      type="checkbox"
                      id="chk01"
                      className="adm_com_chk"
                      onChange={handleSelectAll}
                      checked={selectedInquiries.length === inquiries.length}
                    />
                    <label htmlFor="chk01" />
                  </div>
                </th>
                <th>번호</th>
                <th>분류</th>
                <th>제목</th>
                <th>답변여부</th>
                <th>작성자</th>
                <th>작성일자</th>
                <th>조회수</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry, index) => (
                <tr key={inquiry.board_seq}>
                  <td>
                    <div className="check">
                      <input
                        type="checkbox"
                        id={`chk${index + 2}`}
                        className="adm_com_chk"
                        checked={selectedInquiries.includes(inquiry.board_seq)}
                        onChange={() => handleSelectInquiry(inquiry.board_seq)}
                      />
                      <label htmlFor={`chk${index + 2}`} />
                    </div>
                  </td>
                  <td>{totalCnt - ((currPage - 1) * 10 + index)}</td>
                  <td>
                    {inquiryTitleList.length > 0 && inquiryTitleList.find(a => a.comm_code === inquiry.board_cate)
                      ? inquiryTitleList.find(a => a.comm_code === inquiry.board_cate).code_nm
                      : ''}
                  </td>
                  <td>{inquiry.board_title}</td>
                  <td className={inquiry.board_content2 ? 'td_green bold' : 'td_red'}>
                    {inquiry.board_content2 ? 'Y' : 'X'}
                  </td>
                  <td>{inquiry.owner_seq_al_user?.user_id || '관리자'}</td>
                  <td>{new Date(inquiry.reg_dtm).toLocaleDateString()}</td>
                  <td>{inquiry.board_hit}</td>
                  <td>
                    <button onClick={() => navigate(`/Manage/Inquiry/Write/${inquiry.board_seq}`)} className="adm_com_btn blue line">답변달기</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paging currentPage={currPage} totalPages={totalPages} onPageChange={handlePageChange} />
      </div>
      <Modal
        open={modalIsOpen}
        close={closeModal}
        type="confirm"
        popSize="s"
        title="삭제 확인"
        description="선택된 문의사항을 삭제하시겠습니까?"
      >
        <div className="btn_container">
          <button className="adm_com_btn blue" onClick={handleDelete}>삭제</button>
          <button className="adm_com_btn gray" onClick={closeModal}>취소</button>
        </div>
      </Modal>

      <ToastContainer />
    </main>
  );
};

export default Inquiry;
